/**
 * Manual Facebook signal capture against the canonical GitHub-main contract.
 * No Meta API, scraping or automatic collection. A moderator copies only the
 * observed facts into FacebookSignalIn; the backend owns normalisation,
 * fingerprinting, event-key generation and moderation state.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Info, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

const SIGNAL_TYPES = [
  { value: "announcement", label: "Evenemang – annonsering" },
  { value: "update", label: "Evenemang – ändring" },
  { value: "cancellation", label: "Evenemang – inställt" },
  { value: "result", label: "Evenemang – resultat" },
  { value: "after_report", label: "Efterrapport" },
  { value: "media", label: "Bild / media" },
  { value: "other", label: "Annat" },
];

const EMPTY = {
  signal_type: "announcement",
  facebook_url: "",
  text: "",
  title: "",
  event_date: "",
  event_time: "",
  place: "",
  organiser: "",
  note: "",
};

export default function FacebookSignalDialog({ source, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!source) return null;

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    const observedText = form.text.trim();
    if (!observedText) {
      setError("Klistra in eller skriv den faktiskt observerade texten.");
      return;
    }

    const observedUrl = form.facebook_url.trim() || source.url;
    const rawMetadata = {
      input_mode: "manual_moderator",
      registry_source_name: source.namn || null,
      registry_source_url: source.url || null,
      registry_source_type: source.typ || null,
      monitoring_mode: source.monitoring_mode || null,
      region: source.region || null,
      note: form.note.trim() || null,
    };

    const payload = {
      source_id: source.id,
      source_url: observedUrl,
      signal_type: form.signal_type,
      title: form.title.trim() || null,
      text: observedText,
      event_date: form.event_date || null,
      event_time: form.event_time.trim() || null,
      place: form.place.trim() || null,
      organiser: form.organiser.trim() || null,
      raw_metadata: rawMetadata,
    };

    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/mod/facebook-signals/manual`, payload, {
        headers: modHeaders(),
      });
      if (data?.duplicate) {
        toast.info("Signalen fanns redan – ingen dublett skapades.");
      } else {
        toast.success("Signal sparad för granskning.");
      }
      onSaved?.(data);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Kunde inte spara signal.";
      setError(typeof detail === "string" ? detail : "Kunde inte spara signal.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-parchment border border-warm-border rounded-sm shadow-xl max-w-xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-warm-border px-5 py-3">
          <h3 className="font-heading text-base font-medium text-ink">
            Registrera Facebook-signal – <span className="text-pine">{source.namn}</span>
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-ink" aria-label="Stäng">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-warm-border bg-warm-cream/40 text-xs text-bark">
          <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-pine hover:underline inline-flex items-center gap-1 break-all">
            {source.url} <ExternalLink size={10} />
          </a>
          <p className="mt-2 flex items-start gap-1 text-[11px]">
            <Info size={12} className="mt-0.5 flex-shrink-0" />
            <span>Manuell observation. VIDÄLV hämtar inget från Facebook här; endast det du själv matar in skickas till granskningskön.</span>
          </p>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-ink mb-1">Signaltyp</label>
            <select value={form.signal_type} onChange={(e) => set("signal_type", e.target.value)} className="w-full text-sm px-3 py-2 border border-warm-border rounded-sm bg-white">
              {SIGNAL_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink mb-1">Facebook-URL (valfri)</label>
            <input value={form.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} placeholder="Länk till inlägg/event – annars används källans URL" className="w-full text-sm px-3 py-2 border border-warm-border rounded-sm bg-white" />
          </div>

          <div>
            <label className="block text-xs font-medium text-ink mb-1">Observerad text *</label>
            <textarea value={form.text} onChange={(e) => set("text", e.target.value)} rows={5} required placeholder="Klistra in den relevanta text som du faktiskt ser…" className="w-full text-sm px-3 py-2 border border-warm-border rounded-sm bg-white" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Titel" className="text-sm px-3 py-2 border border-warm-border rounded-sm bg-white" />
            <input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className="text-sm px-3 py-2 border border-warm-border rounded-sm bg-white" />
            <input value={form.event_time} onChange={(e) => set("event_time", e.target.value)} placeholder="Tid, t.ex. 19:00" className="text-sm px-3 py-2 border border-warm-border rounded-sm bg-white" />
            <input value={form.place} onChange={(e) => set("place", e.target.value)} placeholder="Plats" className="text-sm px-3 py-2 border border-warm-border rounded-sm bg-white" />
            <input value={form.organiser} onChange={(e) => set("organiser", e.target.value)} placeholder="Arrangör" className="sm:col-span-2 text-sm px-3 py-2 border border-warm-border rounded-sm bg-white" />
            <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} placeholder="Intern notering (valfri)" className="sm:col-span-2 text-sm px-3 py-2 border border-warm-border rounded-sm bg-white" />
          </div>

          {error && <p className="text-xs text-red-700">{error}</p>}

          <div className="flex justify-end gap-2 pt-2 border-t border-warm-border">
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-warm-border text-bark rounded-sm">Avbryt</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-pine bg-pine text-cream rounded-sm disabled:opacity-50">
              <Send size={12} /> {busy ? "Sparar…" : "Spara signal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
